<template>
    <div class="fulfillment-delivery-content">
        <section class="vertical-align">
            <label class="delivery-title">{{ $t('funnel.edit.fulfillment.delivery.title') }}</label>
            <p>{{ $t('funnel.edit.fulfillment.delivery.summary') }}</p>
        </section>
        <section class="vertical-align">
            <DsButtonToggle
                :value="fulfillmentObject.deliveryType"
                :options="deliveryTypeOptions"
                prevent-single-deselect
                @input="updateFulfillmentObject({...fulfillmentObject, deliveryType: $event})"
            />
        </section>
        <section v-if="emailDeliveryTypeEnabled">
            <div
                class="vertical-align"
                data-qa="email-delivery-container"
            >
                <label class="delivery-subtitle">{{ $t('funnel.edit.fulfillment.delivery.email.subtitle') }}</label>
                <DsMultiselect
                    :value="fulfillmentObject.fromKey"
                    data-qa="fulfillment-email-from"
                    :placeholder="$t('funnel.edit.fulfillment.delivery.email.from')"
                    class="size-lg"
                    bind-value-only
                    :required="true"
                    :options="fromOptions"
                    :force-invalid="forceInvalid && !fulfillmentObject.fromKey"
                    @input="updateFulfillmentObject({...fulfillmentObject, fromKey: $event})"
                />
                <div v-if="fromOtherEnabled" class="horizontal-align">
                    <DsInputField
                        :value="fulfillmentObject.fromName"
                        data-qa="fulfillment-email-from-name"
                        :placeholder="$t('funnel.edit.fulfillment.delivery.email.name')"
                        :label="$t('funnel.edit.fulfillment.delivery.email.name')"
                        @input="updateFulfillmentObject({...fulfillmentObject, fromName: $event})"
                    />
                    <DsInputField
                        :value="fulfillmentObject.fromEmail"
                        data-qa="fulfillment-email-from-email"
                        type="email"
                        :placeholder="$t('funnel.edit.fulfillment.delivery.email.email')"
                        :label="$t('funnel.edit.fulfillment.delivery.email.email')"
                        :required="fromOtherEnabled"
                        :submitted="forceInvalid && fromOtherEnabled && !fulfillmentObject.fromEmail"
                        @input="updateFulfillmentObject({...fulfillmentObject, fromEmail: $event})"
                    />
                </div>
                <DsMultiselect
                    :value="fulfillmentObject.toKey"
                    data-qa="fulfillment-email-to"
                    :placeholder="$t('funnel.edit.fulfillment.delivery.email.to')"
                    class="size-lg"
                    bind-value-only
                    :required="true"
                    :options="toOptions"
                    :force-invalid="forceInvalid && !fulfillmentObject.toKey"
                    @input="updateFulfillmentObject({...fulfillmentObject, toKey: $event})"
                />
                <div v-if="toOtherEnabled" class="horizontal-align">
                    <DsInputField
                        :value="fulfillmentObject.toName"
                        data-qa="fulfillment-email-to-name"
                        :placeholder="$t('funnel.edit.fulfillment.delivery.email.name')"
                        :label="$t('funnel.edit.fulfillment.delivery.email.name')"
                        @input="updateFulfillmentObject({...fulfillmentObject, toName: $event})"
                    />
                    <DsInputField
                        :value="fulfillmentObject.toEmail"
                        data-qa="fulfillment-email-to-email"
                        type="email"
                        :placeholder="$t('funnel.edit.fulfillment.delivery.email.email')"
                        :label="$t('funnel.edit.fulfillment.delivery.email.email')"
                        :required="toOtherEnabled"
                        :submitted="forceInvalid && toOtherEnabled && !fulfillmentObject.toEmail"
                        @input="updateFulfillmentObject({...fulfillmentObject, toEmail: $event})"
                    />
                </div>
                <DsInputField
                    :value="fulfillmentObject.subject"
                    data-qa="fulfillment-email-subject"
                    :placeholder="$t('funnel.edit.fulfillment.delivery.email.subject')"
                    :label="$t('funnel.edit.fulfillment.delivery.email.subject')"
                    :required="true"
                    :submitted="forceInvalid && !fulfillmentObject.subject"
                    @input="updateFulfillmentObject({...fulfillmentObject, subject: $event})"
                />
                <DsTextAreaField
                    :value="fulfillmentObject.htmlBody"
                    data-qa="fulfillment-email-body"
                    :placeholder="$t('funnel.edit.fulfillment.delivery.email.body')"
                    :label="$t('funnel.edit.fulfillment.delivery.email.body')"
                    :required="true"
                    :submitted="forceInvalid && !fulfillmentObject.htmlBody"
                    @input="updateFulfillmentObject({...fulfillmentObject, htmlBody: $event})"
                />
            </div>
        </section>
        <section v-else>
            <div
                class="vertical-align"
                data-qa="dashboard-delivery-container"
            >
                <label class="delivery-subtitle">{{ $t('funnel.edit.fulfillment.delivery.dashboard.subtitle') }}</label>
                <DsMultiselect
                    :value="fulfillmentObject.assignedUserId"
                    :placeholder="$t('funnel.edit.fulfillment.delivery.dashboard.assign')"
                    class="size-lg"
                    data-qa="fulfillment-assigned-user"
                    bind-value-only
                    :required="true"
                    :options="usersOptions"
                    :force-invalid="forceInvalid && !fulfillmentObject.assignedUserId"
                    @input="updateFulfillmentObject({...fulfillmentObject, assignedUserId: $event})"
                />
                <DsTextAreaField
                    :value="fulfillmentObject.instructions"
                    data-qa="fulfillment-instructions"
                    :placeholder="$t('funnel.edit.fulfillment.delivery.dashboard.instructions')"
                    :label="$t('funnel.edit.fulfillment.delivery.dashboard.instructions')"
                    :required="true"
                    :submitted="forceInvalid && !fulfillmentObject.instructions"
                    @input="updateFulfillmentObject({...fulfillmentObject, instructions: $event})"
                />
                <DsCheckbox
                    :value="fulfillmentObject.notify"
                    data-qa="fulfillment-notify"
                    :label="$t('funnel.edit.fulfillment.delivery.dashboard.notify')"
                    @input="updateFulfillmentObject({...fulfillmentObject, notify: $event})"
                />
            </div>
        </section>
        <section>
            <div class="vertical-align">
                <label class="delivery-subtitle">{{ $t('funnel.edit.fulfillment.delivery.attachments.add') }}</label>
                <DsInlineAlert
                    v-if="fileTypeAttachmentsEnabled && forceInvalid && fileSpaceLimitReached"
                    data-qa="fulfillment-attachment-usage-limit"
                    leading-icon
                    type="critical"
                >
                    {{ $t('funnel.edit.fulfillment.delivery.attachments.used.error') }}
                </DsInlineAlert>
                <div class="bottom-gap">
                    <FulfillmentAttachmentFile
                        v-for="(element, index) in (attachments)"
                        :key="index"
                        :data-qa="`fulfillment-attachments-${index}`"
                        :file="element"
                        @delete-file="deleteFile"
                    />
                </div>
                <div class="horizontal-align" :class="{ 'bottom-spacing': !fileTypeAttachmentsEnabled }">
                    <span
                        v-if="emailDeliveryTypeEnabled"
                        class="item-label"
                    >
                        {{ $t('funnel.edit.fulfillment.delivery.attachments.type') }}
                    </span>
                    <DsRadio
                        v-if="emailDeliveryTypeEnabled"
                        :value="fulfillmentObject.attachmentType"
                        data-qa="fulfillment-attachment-link"
                        :val="$options.FULFILLMENT_ATTACHMENT_TYPE.LINK"
                        :label="$t('funnel.edit.fulfillment.delivery.attachments.link')"
                        @input="updateFulfillmentObject({...fulfillmentObject, attachmentType: $event})"
                    />
                    <DsRadio
                        v-if="emailDeliveryTypeEnabled"
                        :value="fulfillmentObject.attachmentType"
                        data-qa="fulfillment-attachment-file"
                        :val="$options.FULFILLMENT_ATTACHMENT_TYPE.FILE"
                        :label="$t('funnel.edit.fulfillment.delivery.attachments.file')"
                        @input="updateFulfillmentObject({...fulfillmentObject, attachmentType: $event})"
                    />
                </div>
                <DsProgressBar
                    v-if="fileTypeAttachmentsEnabled"
                    :class="{ 'limit-alert': fileSpaceLimitReached }"
                    :label="$t('funnel.edit.fulfillment.delivery.attachments.used')"
                    :progress="fileUsage"
                    :show-progress="true"
                    :hide-label-value="true"
                />
                <DsFileUploadInput
                    id="file-upload-input"
                    ref="input"
                    :value="file"
                    :max-file-size-bytes="$options.MAX_FILE_SIZE"
                    :label="$t('funnel.edit.fulfillment.delivery.attachments.upload')"
                    :allowed-files-label="$t('funnel.edit.fulfillment.delivery.attachments.types')"
                    @input="handleInput"
                    @error="handleError"
                />
                <p class="vertical-gap">
                    {{ $t('funnel.edit.fulfillment.delivery.attachments.or') }}
                </p>
                <div class="horizontal-align horizontal-center">
                    <DsMultiselect
                        v-model="attachment"
                        :placeholder="$t('funnel.edit.fulfillment.delivery.attachments.filebox')"
                        class="size-lg"
                        bind-value-only
                        :options="availableFiles"
                    />
                    <DsOutlineButton
                        :disabled="!newAttachmentAvailable || fileSpaceLimitReached"
                        class="limit-height"
                        @click="attachFileFromList"
                    >
                        {{ $t('funnel.edit.fulfillment.delivery.attachments.attach') }}
                    </DsOutlineButton>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import {
    FULFILLMENT_DELIVERY_TYPE,
    FULFILLMENT_ATTACHMENT_TYPE,
    FULFILLMENT_ATTACHMENT_PERCENT_THRESHOLD,
} from '../../../../utils/editor.constants';
import FulfillmentAttachmentFile from '../fulfillmentAttachmentFile/FulfillmentAttachmentFile.vue';
import {
    loadFulfillmentAttachmentFiles,
    loadFilesFromFilebox,
    fileUpload,
} from '../fulfillment/fulfillment.api';
import { fromObjectToOptions } from '../fulfillment/fulfillment.util';

export const MB_SIZE = 1024 * 1024;
export const MAX_FILE_SIZE = 10 * MB_SIZE;
export const MAX_FILE_USAGE_SIZE = 2 * MB_SIZE;

export default {
    MAX_FILE_SIZE,
    MAX_FILE_USAGE_SIZE,
    FULFILLMENT_ATTACHMENT_TYPE,
    FULFILLMENT_DELIVERY_TYPE,
    FULFILLMENT_ATTACHMENT_PERCENT_THRESHOLD,

    components: {
        FulfillmentAttachmentFile,
    },


    props: {
        cell: {
            type: Object,
            required: true,
        },
        fulfillmentObject: {
            type: Object,
            required: true,
        },
        fulfillmentOptions: {
            type: Object,
            required: true,
        },
        forceInvalid: {
            type: Boolean,
            required: true,
        },
    },

    emits: ['update-file-usage', 'update-fulfillment-object'],

    data() {
        return {
            fileLoading: false,
            fileUsage: 0,
            file: null,
            attachments: null,
            filesFromFilebox: [],
            attachment: null,
        };
    },

    computed: {
        emailDeliveryTypeEnabled() {
            return this.fulfillmentObject.deliveryType === FULFILLMENT_DELIVERY_TYPE.EMAIL;
        },

        deliveryTypeOptions() {
            return [
                {
                    value: FULFILLMENT_DELIVERY_TYPE.DASHBOARD,
                    label: this.$t('funnel.edit.fulfillment.delivery.dashboard'),
                },
                {
                    value: FULFILLMENT_DELIVERY_TYPE.EMAIL,
                    label: this.$t('funnel.edit.fulfillment.delivery.email'),
                },
            ];
        },

        toOptions() {
            return fromObjectToOptions(this.fulfillmentOptions?.toMap || {});
        },

        toOtherEnabled() {
            return this.fulfillmentObject.toKey === FULFILLMENT_DELIVERY_TYPE.OTHER_FROM_TYPE;
        },

        fromOptions() {
            return fromObjectToOptions(this.fulfillmentOptions?.fromMap || {});
        },

        fromOtherEnabled() {
            return this.fulfillmentObject.fromKey === FULFILLMENT_DELIVERY_TYPE.OTHER_FROM_TYPE;
        },

        usersOptions() {
            return fromObjectToOptions(this.fulfillmentOptions?.usersMap || {});
        },

        availableFiles() {
            return this.filesFromFilebox.map((element) => ({
                value: element.fileBoxId,
                label: element.fileName,
            }));
        },

        newAttachmentAvailable() {
            return this.attachment !== null;
        },

        fileTypeAttachmentsEnabled() {
            return this.emailDeliveryTypeEnabled
                && this.fulfillmentObject.attachmentType === FULFILLMENT_ATTACHMENT_TYPE.FILE;
        },

        fileSpaceLimitReached() {
            return this.fileUsage === FULFILLMENT_ATTACHMENT_PERCENT_THRESHOLD && this.fileTypeAttachmentsEnabled;
        },
    },

    async mounted() {
        if (this.cell.value?.marketingFulfillmentId) {
            this.attachments = await loadFulfillmentAttachmentFiles(this.cell.value?.marketingFulfillmentId);
            this.calculateFileSpaceUsage();
        }
        this.filesFromFilebox = await loadFilesFromFilebox();
    },

    methods: {
        updateFulfillmentObject(value) {
            this.$emit('update-fulfillment-object', value);
        },

        async handleInput(file) {
            if (file === null) {
                this.fileLoading = false;

                return;
            }

            const ref = this.$refs.input;

            ref.complete = false;
            this.fileLoading = true;

            const formData = new FormData();

            formData.append('file', file);
            formData.append('fileType', 'Application');

            try {
                if (this.fileSpaceLimitReached) {
                    const error = {
                        message: this.$t('funnel.edit.fulfillment.saving.error.limit'),
                    };

                    throw error;
                }
                const fileUploadResponse = await fileUpload(formData);
                const newAttachment = {
                    fileBoxId: Number(fileUploadResponse.fileBoxId),
                    fileName: file.name,
                    fileSize: Number(file.size),
                };

                this.updateFulfillmentObject({
                    ...this.fulfillmentObject,
                    fileBoxIds: [...this.fulfillmentObject.fileBoxIds, newAttachment.fileBoxId],
                });
                this.attachments.push(newAttachment);
                this.filesFromFilebox.push(newAttachment);
                this.calculateFileSpaceUsage();

                ref.success = true;
            } catch (error) {
                ref.success = false;
                this.$error({
                    message: error.message || this.$t('unlayerBuilder.fileUpload.error.uploadingFiles'),
                    bottom: true,
                });
            } finally {
                ref.complete = true;
                this.fileLoading = false;
                this.file = null;
            }
        },

        handleError(error) {
            const ref = this.$refs.input;

            ref.error = true;
            ref.errorMessage = error === 'fileSize'
                ? this.$t('unlayerBuilder.fileUpload.error.maxFileSizeExceeded')
                : this.$t('unlayerBuilder.fileUpload.error.uploadingFiles');
            ref.uploadStarted = true;
        },

        deleteFile(fileId) {
            this.attachments = this.attachments.filter((element) => {
                return element.fileBoxId !== fileId;
            });
            this.updateFulfillmentObject({
                ...this.fulfillmentObject,
                fileBoxIds: this.fulfillmentObject.fileBoxIds.filter((value) => value !== fileId),
            });
            this.calculateFileSpaceUsage();
        },

        attachFileFromList() {
            const response = this.filesFromFilebox.filter((element) => {
                return element.fileBoxId === this.attachment;
            });

            if (response && response.length > 0) {
                const fileToAdd = response[0];

                if (!this.attachments.some((item) => item.fileBoxId === fileToAdd.fileBoxId)) {
                    this.attachments.push(fileToAdd);
                    this.updateFulfillmentObject({
                        ...this.fulfillmentObject,
                        fileBoxIds: [...this.fulfillmentObject.fileBoxIds, fileToAdd.fileBoxId],
                    });
                }
            }
            this.calculateFileSpaceUsage();

            this.attachment = null;
        },

        calculateFileSpaceUsage() {
            let usedSize = 0;

            for (const file of this.attachments) {
                if ('fileSize' in file) {
                    usedSize += file.fileSize;
                }
            }

            this.fileUsage = Math.ceil((usedSize / MAX_FILE_USAGE_SIZE) * FULFILLMENT_ATTACHMENT_PERCENT_THRESHOLD);
            this.fileUsage = this.fileUsage > FULFILLMENT_ATTACHMENT_PERCENT_THRESHOLD
                ? FULFILLMENT_ATTACHMENT_PERCENT_THRESHOLD
                : this.fileUsage;
            this.$emit('update-file-usage', this.fileUsage);
        },
    },
};
</script>
<style lang="scss" scoped>
.fulfillment-delivery-content {
    max-width: px-to-rem(592);

    section {
        padding-bottom: $spacing-300;
    }

    .size-md {
        width: 45%;
    }

    .size-lg {
        width: 65%;
    }

    .bottom-spacing {
        padding-bottom: $spacing-200;
    }

    .bottom-gap {
        padding-bottom: $spacing-300;
    }

    .vertical-gap {
        margin-top: $spacing-300;
        margin-bottom: $spacing-300;
    }

    .horizontal-center {
        width: 100%;
        display: flex;
        justify-content: center;

        .limit-height {
            height: fit-content;
        }
    }

    .vertical-align {
        label.delivery-subtitle {
            font-size: $font-size-lg;
            font-weight: 400;
            line-height: $line-height-lg;
            padding-bottom: $spacing-200;
        }
    }

    .item-label {
        margin: $rem-base 0;
    }

    .horizontal-align {
        display: flex;
        column-gap: $spacing-200;

    }

    label.delivery-title {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 auto;
        font-size: $font-size-jumbo;
        font-weight: 400;
        line-height: $line-height-xxl;
        padding-bottom: $spacing-100;
    }
    p {
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        margin: 0 auto;
        font-size: $font-size-md;
        font-weight: 400;
        line-height: $line-height-lg;
    }
}
</style>
<style lang="scss">
#fulfillment-process-modal div.progress-bar.limit-alert div.bar-container div.bar {
    background-color: $color-red-500;
}
</style>
